import { debounce } from 'lodash';
import { GOGO_DEBUG_LOG } from './constants';
import { shallAIPageKeepAlive } from './utils/keep-alive';
let vue3Router;
let vue2Router;
let baseInfo = {
    base: '/',
    env: 'dev',
    projectId: '',
    branch: '',
};
export function setVue3RouterBaseInfo(info) {
    baseInfo = info;
}
/**
 * forcePath 内嵌场景：判断 vue3 Memory Router 是否已与外层 vue2 当前 URL 对齐。
 * 无 vue2 router 时视为已对齐，避免孤儿/非 runtime 场景死等。
 */
export function isVue3SyncedWithVue2(vue3FullPath) {
    if (!vue2Router)
        return true;
    const vue2FullPath = vue2Router.currentRoute?.fullPath;
    return !!vue2FullPath && vue3FullPath === vue2FullPath;
}
export function createRouterForGogo(router4Module, Vue3) {
    if (vue3Router)
        return vue3Router;
    const { createRouter, createMemoryHistory, START_LOCATION } = router4Module;
    const { h, watchEffect, effectScope } = Vue3;
    if (GOGO_DEBUG_LOG) {
        console.log('[vue3router][gogo] createRouterForGogo', baseInfo);
    }
    vue3Router = createRouter({
        history: createMemoryHistory(baseInfo.base),
        routes: [
            // TODO 虽然 gogo 会动态注册，但是这可能也要一些404之类的默认页面
            { path: '/', component: { render: () => h('div', ['404']) } },
        ],
    });
    // 做绑定的事情
    vue2Router = window.xy_runtime_router; // ./src/router/runtimeModeRouter.js
    if (vue2Router) {
        if (lastVue3SyncPayload)
            notifyVue3RouterChanged(lastVue3SyncPayload);
        const scope = effectScope(true);
        scope.run(() => {
            const debouncedVue2Push = debounce(vue2Router.push.bind(vue2Router), 0, { trailing: true });
            vue3Router.beforeEach((to, from) => {
                if (isSyncing || !lastVue3SyncPayload)
                    return;
                // 无界场景：runtime 卸载时 vue-router4 会把 currentRoute 重置为 START_LOCATION，
                // 重新 mount 执行 app.use(router) 时又会从 START_LOCATION 发起一次初始导航。
                // 这种导航的 from 没有任何匹配记录（matched 为空），并非用户行为，
                // 不能反向 push vue2，否则会把页面重置回首页。此时让 vue3 跟随 vue2 当前路由。
                if (from === START_LOCATION) {
                    const vue2Path = vue2Router.currentRoute.fullPath;
                    return vue2Path !== to.fullPath ? vue2Path : undefined;
                }
                if (vue2Router.currentRoute.fullPath !== to.fullPath) {
                    debouncedVue2Push.cancel();
                    debouncedVue2Push(to.fullPath);
                }
            });
            const debouncedVue3Push = debounce(vue3Router.push.bind(vue3Router), 0, { trailing: true });
            vue2Router.beforeEach((to, from, next) => {
                if (!(isSyncing || !lastVue3SyncPayload)) {
                    if (vue3Router.currentRoute.value.fullPath !== to.fullPath) {
                        debouncedVue3Push.cancel();
                        debouncedVue3Push(to.fullPath);
                    }
                }
                next();
            });
            if (GOGO_DEBUG_LOG) {
                watchEffect(() => {
                    console.log('[vue-router][gogo] getRoutes', [...vue3Router.getRoutes()]);
                });
            }
        });
    }
    else {
        console.warn('[vue-router][gogo] cannot locate runtime vue-router instance');
    }
    return vue3Router;
}
let isSyncing = false;
let lastVue3SyncPayload = null;
export async function notifyVue3RouterChanged(payload) {
    if (!vue2Router || !vue3Router) {
        // not ready? next time sync
        lastVue3SyncPayload = payload;
        return;
    }
    // 好像没有删除路由的能力，先不管了
    // for (const r of lastVue3SyncPayload?.pages || []) {
    //   vue2Router.addRoute
    // }
    try {
        isSyncing = true;
        const GogoRuntimeRouterView = require('./GogoRuntimeRouterView.vue').default;
        for (const r of payload.pages) {
            const pageId = `[gogo-page]${r.file}`;
            const keepAlive = shallAIPageKeepAlive(r.routePath, r.meta);
            vue2Router.addRoute({
                path: r.routePath,
                name: pageId,
                component: GogoRuntimeRouterView,
                props: {
                    projectId: baseInfo.projectId,
                    pageId,
                },
                meta: {
                    // TODO: 各种 pageAttr 的东西？
                    pageId,
                    pageName: pageId,
                    pageAttr: {
                        navFixTop: 'system',
                        customNav: 'system',
                        customSidebar: 'system',
                        customHeader: 'system',
                        customFooter: 'system',
                        showWatermark: 'system',
                        keepAlive,
                        isMobile: false,
                    },
                    isKeepAlive: keepAlive,
                    layoutType: 'header-sidebar-content',
                    pageType: 'content',
                },
            });
        }
        await vue3Router.replace(vue2Router.currentRoute.fullPath);
    }
    catch (e) {
        console.error('[vue-router][gogo] sync error', e);
    }
    isSyncing = false;
    lastVue3SyncPayload = JSON.parse(JSON.stringify(payload)); // lodash的原型可能有问题
}
/**
 * React 链路的路由注册：只注册 Vue2 路由，不操作 Vue3 Router。
 * React 内部通过 MemoryRouter 自行管理路由。
 */
let lastReactSyncPayload = null;
export function notifyReactRouterChanged(payload) {
    const router = window.xy_runtime_router;
    if (!router) {
        lastReactSyncPayload = payload;
        console.warn('[vue-router][gogo-react] vue2 router not ready, deferring');
        return;
    }
    try {
        const GogoReactRuntimeRouterView = require('./GogoReactRuntimeRouterView.vue').default;
        for (const r of payload.pages) {
            const pageId = `[gogo-page]${r.file}`;
            const keepAlive = shallAIPageKeepAlive(r.routePath, r.meta);
            router.addRoute({
                path: r.routePath,
                name: pageId,
                component: GogoReactRuntimeRouterView,
                props: {
                    projectId: baseInfo.projectId,
                    pageId,
                },
                meta: {
                    pageId,
                    pageName: pageId,
                    pageAttr: {
                        navFixTop: 'system',
                        customNav: 'system',
                        customSidebar: 'system',
                        customHeader: 'system',
                        customFooter: 'system',
                        showWatermark: 'system',
                        keepAlive,
                        isMobile: false,
                    },
                    isKeepAlive: keepAlive,
                    layoutType: 'header-sidebar-content',
                    pageType: 'content',
                },
            });
        }
        lastReactSyncPayload = JSON.parse(JSON.stringify(payload));
    }
    catch (e) {
        console.error('[vue-router][gogo-react] sync error', e);
    }
}
